/* eslint-disable react/prop-types */
import { Drawer, IconButton, Tab, Tabs } from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import dayjs from 'dayjs'
import React from 'react'

import { fetchChildHistory } from 'api/apartment'
// The main Parent Dashboard's own drawer styles, so a clicked child opens the
// same panel here as it does there.
import * as styles from 'app/pages/Dashboard/ParentDashboard/components/StudentHistoryDrawer/styles'

/**
 * Per-child history drawer, the apartment twin of StudentHistoryDrawer.
 *
 * That one reads /oh_classes/enrolment and /oh_classes/attendance for centre
 * children. An apartment child's enrolments and attendance live in
 * oh_apartments, so this reads /oh_apartments/children/<uuid>/history/ instead
 * — one call, both tabs.
 */
const TAB_DEFS = [
  { key: 'enrolments', label: 'Enrolments' },
  { key: 'attendance', label: 'Attendance' }
]

const EnrolmentItem = ({ item }) => (
  <styles.Item>
    <styles.ItemTitle>
      <span>{item.program}</span>
      <styles.StatusPill $status={(item.status || '').toLowerCase()}>
        {item.status || '—'}
      </styles.StatusPill>
    </styles.ItemTitle>
    <styles.ItemMeta>
      <span>{item.batch || '—'}</span>
      <span>·</span>
      <span>{item.frequency}</span>
      <span>·</span>
      <span>
        {item.startDate ? dayjs(item.startDate).format('D MMM YY') : '—'}
        {item.endDate ? ` → ${dayjs(item.endDate).format('D MMM YY')}` : ''}
      </span>
    </styles.ItemMeta>
  </styles.Item>
)

const AttendanceItem = ({ item }) => {
  // Tri-state: true / false / null (not yet marked).
  const label =
    item.attended === true
      ? 'attended'
      : item.attended === false
      ? 'absent'
      : 'not marked'
  const status =
    item.attended === true ? 'active' : item.attended === false ? 'closed' : ''
  return (
    <styles.Item>
      <styles.ItemTitle>
        <span>{dayjs(item.date).format('ddd D MMM YY')}</span>
        <styles.StatusPill $status={status}>{label}</styles.StatusPill>
      </styles.ItemTitle>
      <styles.ItemMeta>
        <span>{item.program}</span>
        {item.absenceReason ? (
          <>
            <span>·</span>
            <span>{item.absenceReason}</span>
          </>
        ) : null}
      </styles.ItemMeta>
    </styles.Item>
  )
}

export const ChildHistoryDrawer = ({ open, onClose, child }) => {
  const [tab, setTab] = React.useState('enrolments')
  const [data, setData] = React.useState(null)
  const [loading, setLoading] = React.useState(false)

  const childId = child?.childId

  React.useEffect(() => {
    if (!open || !childId) return undefined
    let cancelled = false
    setLoading(true)
    setData(null)
    fetchChildHistory(childId)
      .then((d) => !cancelled && setData(d))
      .catch(() => !cancelled && setData({ enrolments: [], attendance: [] }))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [open, childId])

  // A newly opened child starts on the first tab.
  React.useEffect(() => {
    setTab('enrolments')
  }, [childId])

  const items = (data && data[tab]) || []

  return (
    <Drawer anchor="right" open={open} onClose={onClose}>
      <styles.DrawerInner>
        <styles.Header>
          <div>
            <styles.HeaderTitle>{child?.name || '—'}</styles.HeaderTitle>
            <styles.HeaderSub>{child?.grade || 'no grade'}</styles.HeaderSub>
          </div>
          <IconButton size="small" onClick={onClose} aria-label="close">
            <CloseIcon fontSize="small" />
          </IconButton>
        </styles.Header>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          {TAB_DEFS.map((t) => (
            <Tab key={t.key} label={t.label} value={t.key} />
          ))}
        </Tabs>
        <styles.Body>
          {loading ? (
            <styles.Loading>loading…</styles.Loading>
          ) : items.length === 0 ? (
            <styles.Empty>no {tab} to show</styles.Empty>
          ) : tab === 'enrolments' ? (
            items.map((it) => <EnrolmentItem key={it.uuid} item={it} />)
          ) : (
            items.map((it) => <AttendanceItem key={it.uuid} item={it} />)
          )}
        </styles.Body>
      </styles.DrawerInner>
    </Drawer>
  )
}

export default ChildHistoryDrawer
